import {
  AuthControllerLoginRequest,
  ResponseError,
} from '@cratehackers/api-client';
import { IonButton, IonInput, IonPage, IonRouterLink } from '@ionic/react';
import * as Sentry from '@sentry/react';
import React, { startTransition, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { config } from '../../../config';
import { on, run } from '../../../helper/electron';
import { useIcons } from '../../../hooks/useIcons';
import ApiService from '../../../services/Api.service';
import PostHogService from '../../../services/PostHog.service';
import { useDispatch } from '../../../store/hooks';
import {
  AppIdentifiers,
  setToken,
} from '../../../store/slices/token-state.slice';
import isElectron from '../../../utils/isElectron';
import { clearLogoutReason, getLogoutReason } from '../../../utils/logout';
import { openExternalLink } from '../../../utils/openExternalLink';
import { openPolicyLink, POLICY_URLS } from '../../../utils/openPolicyLink';
import LoginErrorCard from './LoginErrorCard';
import LoginPlatter from './LoginPlatter';
import '../Auth.scss';
import './login.scss';

interface LoginPageProps {
  setShowLogin?: (showLogin: boolean) => void;
}

const Login: React.FC<LoginPageProps> = ({ setShowLogin }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { register, handleSubmit, getValues, setValue } = useForm();
  const [error, setError] = useState<string>();
  const [logoutMessage, setLogoutMessage] = useState<string>('');
  const [showLogoutMessage, setShowLogoutMessage] = useState<boolean>(false);
  const [isSubscriptionError, setIsSubscriptionError] =
    useState<boolean>(false);
  const { icons } = useIcons();
  const appVersion = config.VERSION ? config.VERSION : 'unknown';
  const isOverlay = Boolean(setShowLogin);

  useEffect(() => {
    const reason = getLogoutReason();
    if (reason && setShowLogin) {
      switch (reason) {
        case 'membership_expired':
          setLogoutMessage(
            'Your membership has expired or is no longer active',
          );
          break;
        case 'session_expired':
          setLogoutMessage('Your session has expired, please login again');
          break;
        default:
          setLogoutMessage('Please login to continue');
      }
      setShowLogoutMessage(true);
      clearLogoutReason();

      const timer = setTimeout(() => {
        setShowLogoutMessage(false);
      }, 7000);

      return () => clearTimeout(timer);
    }
  }, [setShowLogin]);

  useEffect(() => {
    setError('');
    setIsSubscriptionError(false);
  }, []);

  const handleRefreshSession = () => {
    setError('');
    setShowLogoutMessage(false);
    setIsSubscriptionError(false);
    console.log('[Login] Session state refreshed by user');
  };

  useEffect(() => {
    const fetchCredentials = async () => {
      const email = localStorage.getItem('email');
      if (!email || !isElectron()) return;

      run('get-credentials', { email });
      const savedPassword = await new Promise<string>((resolve) => {
        on('get-credentials', (password) => resolve(password));
      });
      if (savedPassword) {
        setValue('email', email);
        setValue('password', savedPassword);
      }
    };

    fetchCredentials();
  }, [setValue]);

  const clearMessages = () => {
    if (error) setError('');
    if (showLogoutMessage) setShowLogoutMessage(false);
    if (isSubscriptionError) setIsSubscriptionError(false);
  };

  const submitForm = async (data: any) => {
    setError('');
    setShowLogoutMessage(false);
    setIsSubscriptionError(false);

    const chApi = ApiService.getApiClient();

    const requestParams: AuthControllerLoginRequest = {
      loginEmailPasswordDto: {
        email: data.email,
        password: data.password,
      },
    };

    chApi
      .authControllerLogin(requestParams)
      .then((data: any) => {
        console.log('🔍 Full user data from API:', data.user);
        console.log('🔍 Available user fields:', Object.keys(data.user || {}));
        if (process.env.REACT_APP_SENTRY_DSN && data.user?.email) {
          Sentry.setUser({ email: data.user?.email });
        }

        if (data.user?.email) {
          localStorage.setItem('email', data.user?.email);
          if (isElectron()) {
            run('save-credentials', {
              email: data.user.email,
              password: getValues('password'),
            });
          }
          const captureLoginEvent = () => {
            PostHogService.capture('user_login', {
              email: data.user.email,
              user_id: data.user.id,
              access_level: data.user.access_level || 'unspecified',
              plan_level: data.user.plan_level || 'standard',
              login_method: 'email_password',
              is_electron: isElectron(),
              app_version: config.VERSION,
            });
          };

          try {
            if (PostHogService.isInitialized()) {
              captureLoginEvent();
            } else {
              setTimeout(() => {
                if (PostHogService.isInitialized()) {
                  captureLoginEvent();
                }
              }, 1000);
            }
          } catch (error) {
            console.error('Error capturing PostHog event:', error);
          }
        }

        startTransition(() => {
          dispatch(
            setToken(
              AppIdentifiers.App,
              data.access_token,
              null,
              Number(data.bubble_expiration),
            ),
          );
          setShowLogin ? setShowLogin(false) : history.push('/');
        });
      })
      .catch(async (error) => {
        console.error('Login error:', error);
        try {
          if (PostHogService.isInitialized()) {
            PostHogService.capture('user_login_failed', {
              error_type:
                error instanceof ResponseError
                  ? 'response_error'
                  : 'network_error',
              is_electron: isElectron(),
              app_version: config.VERSION,
            });
          }
        } catch (err) {
          console.error('Error capturing PostHog login event:', err);
        }

        if (error instanceof ResponseError) {
          const errorBody = await error.response.json();
          console.error('API error:', error.response.status, errorBody);

          if (error.response.status === 401) {
            if (
              errorBody.message?.includes('Inactive account') ||
              errorBody.message?.includes('subscription') ||
              errorBody.message?.includes('membership')
            ) {
              setError(
                'Access denied: Your Crate Hackers membership is inactive or expired. Please renew to continue.',
              );
              setIsSubscriptionError(true);
            } else {
              setError(
                'Invalid email or password. Please check your credentials and try again.',
              );
            }
          } else {
            setError(errorBody.message || 'Login failed. Please try again.');
          }
        } else {
          const errorMessage = error.message || error.toString();

          if (
            errorMessage.includes('Unauthorized') ||
            errorMessage.includes('401')
          ) {
            setError(
              'Access denied: Your Crate Hackers membership is inactive or expired. Please renew to continue.',
            );
            setIsSubscriptionError(true);
          } else if (
            errorMessage.includes('Failed to fetch') ||
            errorMessage.includes('Network')
          ) {
            setError(
              'Unable to connect to server. Please check your internet connection and try again.',
            );
          } else {
            setError(
              'Login failed. Please check your credentials and try again.',
            );
          }
        }
      });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const inputElement = event.target as HTMLInputElement;
      inputElement.blur();

      const formValues = getValues();
      submitForm(formValues);
    }
  };

  const handlePolicyClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    event.preventDefault();
    openPolicyLink(url);
  };

  return (
    <IonPage id="login">
      <form className="login-screen" onSubmit={handleSubmit(submitForm)}>
        <LoginPlatter
          version={appVersion}
          flameSrc={icons.flameLogo}
          handOpenSrc={icons.platterHandOpen}
          handGrabSrc={icons.platterHandGrab}
        />

        <div className="login-column">
          <img
            src={icons.chLogoDarkMode}
            alt="Crate Hackers"
            className="login-logo"
          />

          <div className="login-copy">
            <h1 className="login-headline">
              Drop the
              <br />
              needle.
            </h1>
            <p className="login-sub">
              Your crates, imports and recommendations are spun up and ready.
              Log in to get back on the decks.
            </p>
          </div>

          {(error || (isOverlay && showLogoutMessage)) && (
            <div className="login-alerts">
              {isOverlay && showLogoutMessage ? (
                <div className="login-session">
                  <p className="login-session-text">{logoutMessage}</p>
                  <IonButton
                    fill="clear"
                    size="small"
                    className="login-session-dismiss"
                    onClick={() => setShowLogoutMessage(false)}
                  >
                    ✕
                  </IonButton>
                </div>
              ) : null}
              {error ? (
                <LoginErrorCard
                  error={error}
                  isSubscriptionError={isSubscriptionError}
                  onClear={handleRefreshSession}
                />
              ) : null}
              {isOverlay && showLogoutMessage && !error ? (
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={handleRefreshSession}
                  style={{
                    '--color': 'var(--ion-color-medium)',
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    margin: '0',
                    alignSelf: 'flex-start',
                  }}
                >
                  Clear session message
                </IonButton>
              ) : null}
            </div>
          )}

          <div className="login-fields">
            <div className="login-field login-field-email">
              <span className="login-field-label">Email</span>
              <div className="login-field-shell">
                <IonInput
                  type="email"
                  id="email"
                  aria-label="Email"
                  required
                  placeholder="you@email.com"
                  onKeyPress={handleKeyDown}
                  autocomplete="email"
                  onIonChange={(e) => {
                    setValue('email', e.detail.value || '', {
                      shouldValidate: true,
                    });
                    clearMessages();
                  }}
                  {...register('email')}
                />
              </div>
            </div>

            <div className="login-field login-field-password">
              <div className="login-field-label-row">
                <span className="login-field-label">Password</span>
                {!isOverlay ? (
                  <IonRouterLink
                    routerLink="/forgot-password"
                    className="login-forgot"
                  >
                    Forgot Password?
                  </IonRouterLink>
                ) : null}
              </div>
              <div className="login-field-shell">
                <IonInput
                  type="password"
                  id="password"
                  aria-label="Password"
                  required
                  placeholder="•••••••••"
                  onKeyPress={handleKeyDown}
                  autocomplete="current-password"
                  onIonChange={(e) => {
                    setValue('password', e.detail.value || '', {
                      shouldValidate: true,
                    });
                    clearMessages();
                  }}
                  {...register('password')}
                />
              </div>
            </div>

            <div className="login-actions">
              <div className="login-submit-wrap">
                <IonButton fill="solid" className="login-submit" type="submit">
                  Login
                </IonButton>
              </div>
              {!isOverlay ? (
                <IonButton
                  fill="outline"
                  className="login-signup"
                  type="button"
                  onClick={() =>
                    openExternalLink('https://www.cratehackers.com/')
                  }
                >
                  Sign Up
                </IonButton>
              ) : null}
            </div>
          </div>

          {!isOverlay ? (
            <div className="login-legal">
              <p className="login-legal-copy">
                By signing into this application you agree to the Crate Hackers{' '}
                <a
                  className="login-policy"
                  href={POLICY_URLS.cookie}
                  onClick={(event) =>
                    handlePolicyClick(event, POLICY_URLS.cookie)
                  }
                >
                  Cookie Policy
                </a>
                ,{' '}
                <a
                  className="login-policy"
                  href={POLICY_URLS.terms}
                  onClick={(event) =>
                    handlePolicyClick(event, POLICY_URLS.terms)
                  }
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  className="login-policy"
                  href={POLICY_URLS.privacy}
                  onClick={(event) =>
                    handlePolicyClick(event, POLICY_URLS.privacy)
                  }
                >
                  Privacy Policy
                </a>{' '}
                · Version: {appVersion}
              </p>
            </div>
          ) : null}
        </div>
      </form>
    </IonPage>
  );
};

export default Login;
